import { useState, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import { apiService, ApiError } from '../services/api'
import type { UsageBreakdownData } from '../types'
import { formatNumber } from '../utils/formatters'
import { useDateRange } from '../context/DateRangeContext'
import { useNotification } from '../context/NotificationContext'
import { useLanguage } from '../context/LanguageContext'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

type ChartView = 'app' | 'action' | 'both'

interface UsageChartProps {
  selectedUser?: string
}

export default function UsageChart({ selectedUser = '' }: UsageChartProps) {
  const [view, setView] = useState<ChartView>('app')
  const [data, setData] = useState<UsageBreakdownData | null>(null)
  const [loading, setLoading] = useState(true)
  const { startDate, endDate } = useDateRange()
  const { showNotification } = useNotification()
  const { t } = useLanguage()

  useEffect(() => {
    if (startDate && endDate) {
      loadData()
    }
  }, [startDate, endDate, selectedUser])

  const loadData = async () => {
    setLoading(true)
    try {
      const breakdown = await apiService.getUsageBreakdownByRange(startDate, endDate, selectedUser)
      setData(breakdown)
    } catch (error) {
      console.error('Error loading usage breakdown:', error)

      if (error instanceof ApiError) {
        const [title, ...messageParts] = error.userMessage.split(': ')
        showNotification('error', title, messageParts.join(': '))
      } else {
        showNotification(
          'error',
          t.errors.failedToLoadUsageData,
          t.errors.tryRefreshing
        )
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  // Single blue color for all bars
  const blueColor = '#FBB300' // NextNovate Yellow

  // Prepare chart data based on view
  const getChartData = () => {
    if (view === 'app') {
      const labels = data.by_app.map(item => item.name)
      const counts = data.by_app.map(item => item.count)

      return {
        labels,
        datasets: [
          {
            label: t.usageChart.usage,
            data: counts,
            backgroundColor: blueColor,
            borderColor: blueColor,
            borderWidth: 1,
          },
        ],
      }
    }

    if (view === 'action') {
      const labels = data.by_action.map(item => item.name)
      const counts = data.by_action.map(item => item.count)

      return {
        labels,
        datasets: [
          {
            label: t.usageChart.usage,
            data: counts,
            backgroundColor: blueColor,
            borderColor: blueColor,
            borderWidth: 1,
          },
        ],
      }
    }

    // Both view - show top combinations
    const labels = data.by_both.slice(0, 15).map(item => `${item.app} - ${item.action}`)
    const counts = data.by_both.slice(0, 15).map(item => item.count)

    return {
      labels,
      datasets: [
        {
          label: t.usageChart.usage,
          data: counts,
          backgroundColor: blueColor,
          borderColor: blueColor,
          borderWidth: 1,
        },
      ],
    }
  }

  const options: ChartOptions<'bar'> = {
    indexAxis: 'x' as const,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const index = context.dataIndex
            let tooltipText = `${t.usageChart.usage}: ${formatNumber(context.parsed.y)}`

            if (view === 'app' && data) {
              const users = data.by_app[index]?.users
              tooltipText += ` | ${t.usageChart.users}: ${users}`
            } else if (view === 'action' && data) {
              const users = data.by_action[index]?.users
              tooltipText += ` | ${t.usageChart.users}: ${users}`
            } else if (view === 'both' && data) {
              const users = data.by_both[index]?.users
              tooltipText += ` | ${t.usageChart.users}: ${users}`
            }

            return tooltipText
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatNumber(value as number),
        },
      },
    },
  }

  const chartData = getChartData()

  return (
    <div>
      {/* Tab buttons - minimalistic with line */}
      <div className="flex gap-6 mb-6 border-b border-gray-200">
        <button
          onClick={() => setView('app')}
          className={`pb-3 px-2 font-medium transition-all relative ${
            view === 'app'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t.usageChart.perApp}
          {view === 'app' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
          )}
        </button>
        <button
          onClick={() => setView('action')}
          className={`pb-3 px-2 font-medium transition-all relative ${
            view === 'action'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t.usageChart.perAction}
          {view === 'action' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
          )}
        </button>
        <button
          onClick={() => setView('both')}
          className={`pb-3 px-2 font-medium transition-all relative ${
            view === 'both'
              ? 'text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t.usageChart.perAppAction}
          {view === 'both' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
          )}
        </button>
      </div>

      {/* Chart */}
      <div style={{ height: view === 'both' ? '600px' : '400px' }}>
        <Bar data={chartData} options={options} />
      </div>

      {/* Summary stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {view === 'app' && (
          <>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-gray-600">{t.usageChart.totalApps}</p>
              <p className="text-2xl font-bold text-blue-600">{data.by_app.length}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-gray-600">{t.usageChart.mostUsed}</p>
              <p className="text-lg font-bold text-blue-600">{data.by_app[0]?.name || 'N/A'}</p>
              <p className="text-xs text-gray-500">{formatNumber(data.by_app[0]?.count || 0)} {t.usageChart.times}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-gray-600">{t.usageChart.totalUsage}</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatNumber(data.by_app.reduce((sum, item) => sum + item.count, 0))}
              </p>
            </div>
          </>
        )}
        {view === 'action' && (
          <>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <p className="text-sm text-gray-600">{t.usageChart.totalActions}</p>
              <p className="text-2xl font-bold text-yellow-600">{data.by_action.length}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <p className="text-sm text-gray-600">{t.usageChart.mostPopular}</p>
              <p className="text-lg font-bold text-yellow-600">{data.by_action[0]?.name || 'N/A'}</p>
              <p className="text-xs text-gray-500">{formatNumber(data.by_action[0]?.count || 0)} {t.usageChart.times}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <p className="text-sm text-gray-600">{t.usageChart.totalUsage}</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatNumber(data.by_action.reduce((sum, item) => sum + item.count, 0))}
              </p>
            </div>
          </>
        )}
        {view === 'both' && (
          <>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-gray-600">{t.usageChart.totalCombinations}</p>
              <p className="text-2xl font-bold text-amber-600">{data.by_both.length}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-gray-600">{t.usageChart.topCombination}</p>
              <p className="text-sm font-bold text-amber-600">
                {data.by_both[0]?.app || 'N/A'} - {data.by_both[0]?.action || 'N/A'}
              </p>
              <p className="text-xs text-gray-500">{formatNumber(data.by_both[0]?.count || 0)} {t.usageChart.times}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-gray-600">{t.usageChart.totalUsage}</p>
              <p className="text-2xl font-bold text-amber-600">
                {formatNumber(data.by_both.reduce((sum, item) => sum + item.count, 0))}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
